import Chart from 'react-apexcharts'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Select, Tooltip } from 'antd'
import { ToastContainer } from 'react-toastify'
import { ContainerTitle, DivTitle } from '../../components/TitlePage/TitlePage'
import { GenericTable } from '../../components/GenericTable/GenericTable'
import { Loading } from '../../components/Loading/Loading'
import { Pagination } from '../../components/Pagination/Pagination'
import DatesPicker from '../../components/Calendar/datePicker'
import { colors } from '../../theme'
import { useTopUserData } from '../../components/Top/TopUserData/useTopUserData'
import { usePredictAnalytics } from './usePredictAnalytics'
import { DrillAlvo, useAnalyticsDrill } from './useAnalyticsDrill'
import { DrillDrawer } from './DrillDrawer'
import {
  CardsGrid,
  Container,
  ContadorBotao,
  CountersRow,
  ChartWrapper,
  FilterBar,
  FilterField,
  HintBotao,
  LegendIcon,
  MetricCard,
  MetricHint,
  MetricTitle,
  MetricValueBotao,
  RankingsRow,
  SectionTitle,
  TableWrapper,
} from './PredictAnalytics.style'

// Textos de legenda por indicador (conceito p/ o usuário). Base: docs/06 RN-12.
// Denominador de TODAS as taxas = "avaliadas" (predições com desfecho), não "geradas": ver a
// legenda de `avaliadas` abaixo e a nota do PredicaoSnapshotRepository na API.
const DENOMINADOR =
  'A conta considera apenas as predições avaliadas — as que já têm desfecho. Ficam de fora as que ainda estão dentro da janela de ±7 dias e as que foram substituídas por uma previsão nova antes de esse prazo terminar. Compra atrasada não é substituição: a predição errou e continua contando como erro.'

const LEGENDAS = {
  assertividade: `Percentual das predições avaliadas que viraram venda dentro da janela de ±7 dias da data prevista (realizadas ÷ avaliadas). ${DENOMINADOR} Trabalhadas = conversão das que o vendedor deu baixa; Base = conversão das não trabalhadas; Ganho (lift) = quanto trabalhar a predição aumenta a conversão frente à base.`,
  // Numerador e denominador vêm do MESMO universo (as avaliadas): usar o contador bruto
  // "Baixadas" aqui daria conta diferente da exibida, e passaria de 100%.
  utilizacao: `Percentual das predições avaliadas que o vendedor efetivamente trabalhou, dando baixa (baixadas avaliadas ÷ avaliadas). ${DENOMINADOR}`,
  conversaoUsuario:
    'Das predições avaliadas que o vendedor trabalhou, quantas viraram venda (baixadas e realizadas ÷ baixadas avaliadas). Mede a eficácia da ação do vendedor sobre a predição.',
  perdaNaoUtilizacao: `Percentual de predições avaliadas que não foram trabalhadas — oportunidades sem baixa no período. ${DENOMINADOR}`,
  churnEvitavel:
    'Indicador exploratório: possível oportunidade não trabalhada que não virou venda na janela. Não é venda perdida atribuível a uma pessoa.',
  geradas:
    'Total de predições (episódios cliente×produto) criadas no período, incluindo as ainda em aberto e as substituídas.',
  avaliadas:
    'Predições com desfecho conclusivo — a base de cálculo das taxas. É o total gerado menos as que ainda estão em aberto e as substituídas antes do prazo terminar.',
  baixadas:
    'Volume bruto de predições que o vendedor marcou como trabalhadas (ação de baixa) no período — inclui as que ainda estão em aberto e as substituídas. Por isso não é o numerador da Utilização, que só considera as avaliadas.',
  realizadas:
    'Predições que se confirmaram em venda real na janela de ±7 dias.',
  emAberto:
    'Predições ainda sem desfecho no período: a janela de ±7 dias da data prevista ainda não fechou. Não contam como erro.',
  obsoletas:
    'Predições trocadas por uma previsão nova do mesmo cliente e produto ANTES de o prazo delas terminar — o cliente comprou adiantado, a data foi recalculada e a previsão anterior nunca chegou a ser julgada. Não contam como erro. Compra atrasada não entra aqui: nesse caso o prazo terminou sem venda, a predição errou e continua contando como erro.',
  evolucao:
    'Assertividade e utilização mês a mês, para acompanhar a tendência no período filtrado. Meses sem nenhuma predição avaliada ficam sem ponto na linha.',
  rankingVendedores:
    'Vendedores ordenados pelo volume de realizadas no período (predições atribuídas a eles). A conversão de cada linha é calculada sobre as avaliadas, então a lista não segue a ordem da conversão.',
  clientesPerdidos:
    'Clientes com mais predições avaliadas que não se realizaram no período — foco de atenção comercial. Não inclui predições substituídas por uma previsão mais recente.',
}

// Icone de legenda (badge "?") com o conceito do indicador em um Tooltip.
// tabIndex + aria-label: abre por teclado/touch e o leitor de tela anuncia o conceito.
function LegendTip({ text }: { text: string }) {
  return (
    // O default do antd é max-width 250px: com o texto do denominador as legendas
    // viravam uma coluna estreita e altíssima.
    <Tooltip title={text} overlayStyle={{ maxWidth: 420 }}>
      <LegendIcon role="img" tabIndex={0} aria-label={text}>
        ?
      </LegendIcon>
    </Tooltip>
  )
}

// Fracao 0..1 -> "73,0%". null = sem dado (nao e 0%).
function formatPct(valor: number | null): string {
  if (valor === null || valor === undefined) {
    return 'Sem dado'
  }
  return `${(valor * 100).toFixed(1).replace('.', ',')}%`
}

function pctNumber(valor: number | null): number | null {
  if (valor === null || valor === undefined) {
    return null
  }
  return Math.round(valor * 1000) / 10
}

// PD-0006 — mapeamento de cada quadro para o(s) conjunto(s) que ele abre. É CONTRATO, não
// sugestão: ligar um quadro ao conjunto errado produz um painel que não bate com o número,
// exatamente a falha que o drill existe para tornar impossível. Cada linha aqui espelha a
// fórmula do card em `PredicaoAnalyticsService.metricas`.
// Chaves fechadas em vez de `Record<string, ...>`: com string livre um erro de digitação
// (`alvos.assertivdade`) compilaria e só estouraria em runtime, justamente no mapa que é
// contrato. Assim o compilador cobra a chave certa E a presença de todos os 14 quadros.
type AlvoChave =
  | 'assertividade'
  | 'utilizacao'
  | 'conversaoUsuario'
  | 'perdaNaoUtilizacao'
  | 'churnEvitavel'
  | 'conversaoTrabalhadas'
  | 'conversaoNaoTrabalhadas'
  | 'geradas'
  | 'avaliadas'
  | 'baixadas'
  | 'realizadas'
  | 'emAberto'
  | 'substituidas'

function montarAlvos(
  m: GetAnalyticsMetricasType
): Record<AlvoChave, DrillAlvo> {
  const n = (valor: number) => valor || 0
  return {
    // realizadas ÷ avaliadas
    assertividade: {
      quadro: 'Assertividade',
      numerador: { conjunto: 'REALIZADAS', valor: n(m.realizadas) },
      denominador: { conjunto: 'AVALIADAS', valor: n(m.avaliadas) },
      taxa: m.assertividade,
    },
    // trabalhadas avaliáveis ÷ avaliadas
    utilizacao: {
      quadro: 'Utilização',
      numerador: {
        conjunto: 'TRABALHADAS_AVALIAVEIS',
        valor: n(m.trabalhadasAvaliaveis),
      },
      denominador: { conjunto: 'AVALIADAS', valor: n(m.avaliadas) },
      taxa: m.utilizacao,
    },
    // trabalhadas realizadas ÷ trabalhadas avaliáveis
    conversaoUsuario: {
      quadro: 'Conversão do usuário',
      numerador: {
        conjunto: 'TRABALHADAS_REALIZADAS',
        valor: n(m.baixadasRealizadas),
      },
      denominador: {
        conjunto: 'TRABALHADAS_AVALIAVEIS',
        valor: n(m.trabalhadasAvaliaveis),
      },
      taxa: m.conversaoUsuario,
    },
    // não trabalhadas avaliáveis ÷ avaliadas
    perdaNaoUtilizacao: {
      quadro: 'Perda por não utilização',
      numerador: {
        conjunto: 'NAO_TRABALHADAS_AVALIAVEIS',
        valor: n(m.naoTrabalhadasAvaliaveis),
      },
      denominador: { conjunto: 'AVALIADAS', valor: n(m.avaliadas) },
      taxa: m.perdaNaoUtilizacao,
    },
    // não realizadas vigentes e não trabalhadas ÷ avaliadas
    churnEvitavel: {
      quadro: 'Churn evitável',
      numerador: {
        conjunto: 'CHURN_EVITAVEL',
        valor: n(m.naoRealizadasVigentesNaoTrabalhadas),
      },
      denominador: { conjunto: 'AVALIADAS', valor: n(m.avaliadas) },
      taxa: m.churnEvitavel,
    },
    // Sub-números do card de Assertividade. "Trabalhadas" repete o par da Conversão do
    // usuário; "Base" é o grupo de controle do lift.
    conversaoTrabalhadas: {
      quadro: 'Trabalhadas',
      numerador: {
        conjunto: 'TRABALHADAS_REALIZADAS',
        valor: n(m.baixadasRealizadas),
      },
      denominador: {
        conjunto: 'TRABALHADAS_AVALIAVEIS',
        valor: n(m.trabalhadasAvaliaveis),
      },
      taxa: m.conversaoTrabalhadas,
    },
    conversaoNaoTrabalhadas: {
      quadro: 'Base (não trabalhadas)',
      numerador: {
        conjunto: 'NAO_TRABALHADAS_REALIZADAS',
        valor: n(m.naoTrabalhadasRealizadas),
      },
      denominador: {
        conjunto: 'NAO_TRABALHADAS_AVALIAVEIS',
        valor: n(m.naoTrabalhadasAvaliaveis),
      },
      taxa: m.conversaoNaoTrabalhadas,
    },
    // Contadores: abrem direto, sem denominador.
    geradas: {
      quadro: 'Geradas',
      numerador: { conjunto: 'GERADAS', valor: n(m.geradas) },
    },
    avaliadas: {
      quadro: 'Avaliadas',
      numerador: { conjunto: 'AVALIADAS', valor: n(m.avaliadas) },
    },
    baixadas: {
      quadro: 'Baixadas',
      numerador: { conjunto: 'BAIXADAS_BRUTO', valor: n(m.baixadas) },
    },
    realizadas: {
      quadro: 'Realizadas',
      numerador: { conjunto: 'REALIZADAS', valor: n(m.realizadas) },
    },
    emAberto: {
      quadro: 'Em aberto',
      numerador: { conjunto: 'EM_ABERTO', valor: n(m.emAberto) },
    },
    substituidas: {
      quadro: 'Substituídas',
      numerador: { conjunto: 'SUBSTITUIDAS', valor: n(m.obsoletas) },
    },
  }
}

export function PredictAnalytics() {
  const { userDataOrganization } = useTopUserData()
  const papel = userDataOrganization?.papelOrganizacao
  // ⚠️ Antes de o `/me` responder, `papel` é undefined — e isso NÃO é "perfil sem
  // escopo". As duas flags abaixo são derivadas de forma CONSERVADORA justamente
  // por isso: o default de `papel !== 'VENDEDOR'` seria `true`, e o card do
  // ranking apareceria e sumiria; o default de `temEscopoProprio` seria `false`,
  // e o vendedor leria os números como sendo da organização inteira na primeira
  // pintura da tela — que é exatamente o que o aviso existe para impedir.
  const organizacaoCarregada = userDataOrganization !== undefined
  // Quem tem escopo por vendedor (PD-0004) recebe o painel já recortado pelo
  // backend, sem nada marcado na tela. Sem dizer isso, o usuário leria os
  // números como sendo da organização inteira — e eles não são.
  const temEscopoProprio =
    papel === 'VENDEDOR' || papel === 'ASSISTENTE' || papel === 'COORDENADOR'
  // O ranking compara vendedores entre si. Para o VENDEDOR o escopo tem um
  // vendedor só, então a tabela sairia com uma linha: uma comparação com
  // ninguém. Coordenador e assistente respondem por N e veem a equipe.
  const veRankingDeVendedores = organizacaoCarregada && papel !== 'VENDEDOR'

  const {
    metricas,
    vendedores,
    clientesPerdidos,
    evolucao,
    listaVendedores,
    vendedorId,
    dataInicio,
    dataFim,
    isLoading,
    isLoadingVendedores,
    isLoadingClientes,
    pageVendedores,
    setPageVendedores,
    pageClientes,
    setPageClientes,
    onChangeDataInicio,
    onChangeDataFim,
    onChangeVendedor,
    // Não busca o ranking de vendedores para quem não vai vê-lo (perfil VENDEDOR).
  } = usePredictAnalytics(organizacaoCarregada && papel === 'VENDEDOR')

  // O drill herda o MESMO período e o MESMO vendedor do painel — nunca filtros próprios.
  const drill = useAnalyticsDrill({ dataInicio, dataFim, vendedorId })
  const alvos = montarAlvos(metricas)

  const chartSeries = [
    {
      name: 'Assertividade',
      data: evolucao.map(item => pctNumber(item.assertividade)),
    },
    {
      name: 'Utilização',
      data: evolucao.map(item => pctNumber(item.utilizacao)),
    },
  ]

  // Mês sem nenhuma predição avaliada vem null (não 0%), então a série pode estar
  // inteiramente vazia mesmo com meses retornados. Sem esta checagem o Apex desenharia
  // um gráfico vazio com eixo 0%–5%, que se lê como "assertividade perto de zero" —
  // justamente a leitura errada que a correção do denominador veio desfazer.
  const temSerie = chartSeries.some(serie =>
    serie.data.some(valor => valor !== null)
  )

  const chartOptions: ApexCharts.ApexOptions = {
    // Sem `theme.mode` o ApexCharts mantem o default CLARO nos rotulos de eixo
    // (#373d3f), na legenda, na grade e no tooltip — trocar so o array `colors`
    // das series nao alcanca nada disso.
    theme: { mode: 'dark' },
    chart: {
      toolbar: { show: false },
      background: 'transparent',
      foreColor: colors.grey700,
    },
    grid: { borderColor: colors.grey300 },
    tooltip: { theme: 'dark' },
    // Series distinguem-se por claridade, nao por matiz: `azul2` e `secondary`
    // sao dois verdes medios e ficavam ambiguos na legenda.
    colors: [colors.secondary, colors.lilas],
    stroke: { curve: 'smooth', width: 3 },
    // Marcador visível: com `curve: smooth` um ponto cercado de nulls não gera traço
    // desenhável, e com o default `size: 0` o mês isolado ficaria invisível.
    markers: { size: 4 },
    xaxis: { categories: evolucao.map(item => item.periodo) },
    yaxis: { labels: { formatter: valor => `${valor}%` } },
    legend: { position: 'top' },
    dataLabels: { enabled: false },
  }

  return (
    <Container>
      <ToastContainer theme="dark" />
      <ContainerTitle>
        <DivTitle color={colors.primaryText} size={24}>
          Predict Analytics
        </DivTitle>
      </ContainerTitle>

      {temEscopoProprio && (
        <MetricHint>
          Estes números são <strong>só da sua carteira</strong> — as predições
          atribuídas a você (ou à sua equipe), não as da organização inteira. A
          atribuição é a do vendedor da venda que originou cada predição, então
          cliente que passou a ser seu depois não entra, e predição sem vendedor
          atribuído não aparece para ninguém além do administrador.
        </MetricHint>
      )}

      <FilterBar>
        <FilterField>
          <span>De</span>
          <DatesPicker
            size="150px"
            background
            handleDataSelecionada={onChangeDataInicio}
          />
        </FilterField>
        <FilterField>
          <span>Até</span>
          <DatesPicker
            size="150px"
            background
            handleDataSelecionada={onChangeDataFim}
          />
        </FilterField>
        <FilterField>
          <span>Vendedor</span>
          <Select
            allowClear
            showSearch
            style={{ width: 220 }}
            // "Todos" nunca significa "o tenant inteiro" para quem tem escopo:
            // significa "todos os que você alcança". Prometer o contrário num
            // placeholder é o jeito mais barato de fazer o painel mentir.
            placeholder={
              temEscopoProprio ? 'Todos os meus' : 'Todos os vendedores'
            }
            optionFilterProp="label"
            value={vendedorId === null ? undefined : vendedorId}
            onChange={value => onChangeVendedor(value ?? null)}
            options={listaVendedores.map(item => ({
              value: item.id,
              label: item.nome,
            }))}
          />
        </FilterField>
      </FilterBar>

      {isLoading ? (
        <Loading contrast={false} />
      ) : (
        <>
          <CardsGrid>
            <MetricCard>
              <MetricTitle>
                Assertividade <LegendTip text={LEGENDAS.assertividade} />
              </MetricTitle>
              <MetricValueBotao
                type="button"
                title="Ver as predições que compõem este número"
                onClick={() => drill.abrir(alvos.assertividade)}
              >
                {formatPct(metricas.assertividade)}
              </MetricValueBotao>
              <MetricHint>
                Trabalhadas:{' '}
                <HintBotao
                  type="button"
                  onClick={() => drill.abrir(alvos.conversaoTrabalhadas)}
                >
                  {formatPct(metricas.conversaoTrabalhadas)}
                </HintBotao>{' '}
                · Base (não trabalhadas):{' '}
                <HintBotao
                  type="button"
                  onClick={() => drill.abrir(alvos.conversaoNaoTrabalhadas)}
                >
                  {formatPct(metricas.conversaoNaoTrabalhadas)}
                </HintBotao>
              </MetricHint>
              {/* Ganho (lift) não abre lista: é a diferença entre as duas taxas acima,
                  não um conjunto de predições. */}
              <MetricHint>Ganho (lift): {formatPct(metricas.lift)}</MetricHint>
            </MetricCard>

            <MetricCard>
              <MetricTitle>
                Utilização <LegendTip text={LEGENDAS.utilizacao} />
              </MetricTitle>
              <MetricValueBotao
                type="button"
                title="Ver as predições que compõem este número"
                onClick={() => drill.abrir(alvos.utilizacao)}
              >
                {formatPct(metricas.utilizacao)}
              </MetricValueBotao>
              <MetricHint>Predições trabalhadas (baixadas)</MetricHint>
            </MetricCard>

            <MetricCard>
              <MetricTitle>
                Conversão do usuário{' '}
                <LegendTip text={LEGENDAS.conversaoUsuario} />
              </MetricTitle>
              <MetricValueBotao
                type="button"
                title="Ver as predições que compõem este número"
                onClick={() => drill.abrir(alvos.conversaoUsuario)}
              >
                {formatPct(metricas.conversaoUsuario)}
              </MetricValueBotao>
              <MetricHint>Trabalhadas que viraram venda</MetricHint>
            </MetricCard>

            <MetricCard>
              <MetricTitle>
                Perda por não utilização{' '}
                <LegendTip text={LEGENDAS.perdaNaoUtilizacao} />
              </MetricTitle>
              <MetricValueBotao
                type="button"
                title="Ver as predições que compõem este número"
                onClick={() => drill.abrir(alvos.perdaNaoUtilizacao)}
              >
                {formatPct(metricas.perdaNaoUtilizacao)}
              </MetricValueBotao>
              <MetricHint>Predições não trabalhadas</MetricHint>
            </MetricCard>

            <MetricCard>
              <MetricTitle>
                Churn evitável <LegendTip text={LEGENDAS.churnEvitavel} />
              </MetricTitle>
              <MetricValueBotao
                type="button"
                title="Ver as predições que compõem este número"
                onClick={() => drill.abrir(alvos.churnEvitavel)}
              >
                {formatPct(metricas.churnEvitavel)}
              </MetricValueBotao>
              <MetricHint>Possível oportunidade não trabalhada</MetricHint>
            </MetricCard>
          </CardsGrid>

          {/* Geradas = avaliadas + em aberto + substituídas. Os três últimos explicam por que o
              denominador das taxas é menor que o volume gerado. */}
          <CountersRow>
            <span>
              Geradas:{' '}
              <ContadorBotao
                type="button"
                title="Ver as predições que compõem este número"
                onClick={() => drill.abrir(alvos.geradas)}
              >
                {metricas.geradas || 0}
              </ContadorBotao>{' '}
              <LegendTip text={LEGENDAS.geradas} />
            </span>
            <span>
              Avaliadas:{' '}
              <ContadorBotao
                type="button"
                title="Ver as predições que compõem este número"
                onClick={() => drill.abrir(alvos.avaliadas)}
              >
                {metricas.avaliadas || 0}
              </ContadorBotao>{' '}
              <LegendTip text={LEGENDAS.avaliadas} />
            </span>
            <span>
              Baixadas:{' '}
              <ContadorBotao
                type="button"
                title="Ver as predições que compõem este número"
                onClick={() => drill.abrir(alvos.baixadas)}
              >
                {metricas.baixadas || 0}
              </ContadorBotao>{' '}
              <LegendTip text={LEGENDAS.baixadas} />
            </span>
            <span>
              Realizadas:{' '}
              <ContadorBotao
                type="button"
                title="Ver as predições que compõem este número"
                onClick={() => drill.abrir(alvos.realizadas)}
              >
                {metricas.realizadas || 0}
              </ContadorBotao>{' '}
              <LegendTip text={LEGENDAS.realizadas} />
            </span>
            <span>
              Em aberto:{' '}
              <ContadorBotao
                type="button"
                title="Ver as predições que compõem este número"
                onClick={() => drill.abrir(alvos.emAberto)}
              >
                {metricas.emAberto || 0}
              </ContadorBotao>{' '}
              <LegendTip text={LEGENDAS.emAberto} />
            </span>
            <span>
              Substituídas:{' '}
              <ContadorBotao
                type="button"
                title="Ver as predições que compõem este número"
                onClick={() => drill.abrir(alvos.substituidas)}
              >
                {metricas.obsoletas || 0}
              </ContadorBotao>{' '}
              <LegendTip text={LEGENDAS.obsoletas} />
            </span>
          </CountersRow>
        </>
      )}

      <SectionTitle>
        Evolução no período <LegendTip text={LEGENDAS.evolucao} />
      </SectionTitle>
      <ChartWrapper>
        {evolucao.length > 0 && temSerie ? (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={300}
          />
        ) : (
          <MetricHint>
            {evolucao.length > 0
              ? 'Nenhuma predição avaliada no período — ainda sem série. As predições em aberto entram no gráfico quando a janela de ±7 dias fechar.'
              : 'Sem dados no período selecionado.'}
          </MetricHint>
        )}
      </ChartWrapper>

      <RankingsRow>
        {/* Com filtro por vendedor o ranking colapsa para o proprio vendedor: card ocultado.
            Mesma razao para o perfil VENDEDOR, cujo escopo tem um vendedor so. */}
        {!vendedorId && veRankingDeVendedores && (
          <div>
            <SectionTitle>
              Ranking de vendedores{' '}
              <LegendTip text={LEGENDAS.rankingVendedores} />
            </SectionTitle>
            <TableWrapper>
              <GenericTable
                font="14px"
                spacing="16px"
                vendas
                // Geradas = volume atribuído ao vendedor; Avaliadas = denominador da
                // conversão exibida ao lado. As duas juntas deixam a conta conferível na
                // linha e expõem quando a base avaliada é pequena demais para concluir algo.
                headers={[
                  'Vendedor',
                  'Geradas',
                  'Avaliadas',
                  'Realizadas',
                  'Conversão',
                ]}
              >
                {isLoadingVendedores ? (
                  <Loading contrast={false} />
                ) : (
                  <>
                    {Array.isArray(vendedores.content) &&
                      vendedores.content.map(item => (
                        <tr
                          key={
                            item.vendedorId === null
                              ? 'sem-vendedor'
                              : item.vendedorId
                          }
                        >
                          <td>{item.vendedorNome}</td>
                          <td>{item.geradas}</td>
                          <td>{item.avaliadas}</td>
                          <td>{item.realizadas}</td>
                          <td>{formatPct(item.taxaConversao)}</td>
                        </tr>
                      ))}
                    {Array.isArray(vendedores.content) &&
                      vendedores.content.length === 0 && (
                        <tr>
                          <td colSpan={5}>Nenhum dado no período.</td>
                        </tr>
                      )}
                  </>
                )}
              </GenericTable>
            </TableWrapper>
            {vendedores && vendedores.totalElements > 0 && (
              <Pagination
                pageNumber={pageVendedores}
                pageSize={vendedores.totalElements}
                offSet={vendedores.size}
                onClick={page => setPageVendedores(page)}
              />
            )}
          </div>
        )}

        <div>
          <SectionTitle>
            Clientes não convertidos{' '}
            <LegendTip text={LEGENDAS.clientesPerdidos} />
          </SectionTitle>
          <TableWrapper>
            <GenericTable
              font="14px"
              spacing="16px"
              vendas
              headers={['Cliente', 'Predições não realizadas']}
            >
              {isLoadingClientes ? (
                <Loading contrast={false} />
              ) : (
                <>
                  {Array.isArray(clientesPerdidos.content) &&
                    clientesPerdidos.content.map(item => (
                      <tr key={item.clienteId}>
                        <td>{item.clienteNome}</td>
                        <td>{item.naoRealizadas}</td>
                      </tr>
                    ))}
                  {Array.isArray(clientesPerdidos.content) &&
                    clientesPerdidos.content.length === 0 && (
                      <tr>
                        <td colSpan={2}>Nenhum dado no período.</td>
                      </tr>
                    )}
                </>
              )}
            </GenericTable>
          </TableWrapper>
          {clientesPerdidos && clientesPerdidos.totalElements > 0 && (
            <Pagination
              pageNumber={pageClientes}
              pageSize={clientesPerdidos.totalElements}
              offSet={clientesPerdidos.size}
              onClick={page => setPageClientes(page)}
            />
          )}
        </div>
      </RankingsRow>

      {/* Painel lateral do drill: abre sobre o Analytics e devolve o painel intacto ao
          fechar — o card e a lista ficam no mesmo contexto de leitura. */}
      <DrillDrawer
        alvo={drill.alvo}
        conjunto={drill.conjunto}
        dados={drill.dados}
        erro={drill.erro}
        isLoading={drill.isLoading}
        isExportando={drill.isExportando}
        page={drill.page}
        setPage={drill.setPage}
        temEscopoProprio={temEscopoProprio}
        busca={drill.busca}
        setBusca={drill.setBusca}
        ordenacao={drill.ordenacao}
        fechar={drill.fechar}
        alternarLado={drill.alternarLado}
        alternarOrdenacao={drill.alternarOrdenacao}
        exportar={drill.exportar}
      />
    </Container>
  )
}
